import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  Refresh,
  SwapVert,
  Person,
  Receipt,
  Business,
  Payments,
  ArrowBack,
  Visibility,
} from '@mui/icons-material';
import { getAssociates } from '../../services/associate';
import { getProjects } from '../../services/project';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

import { useHistory } from 'react-router-dom';

const TotalDue = () => {
  const history = useHistory();

  // Helper function to load state from sessionStorage
  const loadSavedState = (key, defaultValue) => {
    try {
      const saved = sessionStorage.getItem(`totalDue_${key}`);
      if (saved === null || saved === 'undefined') return defaultValue;
      return JSON.parse(saved);
    } catch (e) {
      return defaultValue;
    }
  };

  // Filters & Sorting state
  const [search, setSearch] = useState(() => loadSavedState('search', ''));
  const [associateId, setAssociateId] = useState(() => loadSavedState('associateId', 'all'));
  const [sortBy, setSortBy] = useState(() => loadSavedState('sortBy', ''));
  const [fromDate, setFromDate] = useState(() => loadSavedState('fromDate', ''));
  const [toDate, setToDate] = useState(() => loadSavedState('toDate', ''));
  const [dateBy, setDateBy] = useState(() => loadSavedState('dateBy', ''));
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(() => loadSavedState('projectId', 'all'));

  // More Filters Dialog States
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [tempSortBy, setTempSortBy] = useState('');
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');
  const [tempDateBy, setTempDateBy] = useState('');
  const [tempProjectId, setTempProjectId] = useState('all');

  // Associates dropdown paginated search states
  const [associates, setAssociates] = useState([]);
  const [associatesPage, setAssociatesPage] = useState(1);
  const [hasMoreAssociates, setHasMoreAssociates] = useState(true);
  const [associatesSearch, setAssociatesSearch] = useState('');
  const [selectedAssociate, setSelectedAssociate] = useState(() => loadSavedState('selectedAssociate', null));

  // Data state
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [error, setError] = useState('');

  // Pagination state
  const [page, setPage] = useState(() => loadSavedState('page', 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => loadSavedState('rowsPerPage', 10));
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalBookingAmount: 0,
    totalReceived: 0,
    totalDue: 0,
  });

  // Save state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('totalDue_search', JSON.stringify(search));
    sessionStorage.setItem('totalDue_associateId', JSON.stringify(associateId));
    sessionStorage.setItem('totalDue_sortBy', JSON.stringify(sortBy));
    sessionStorage.setItem('totalDue_fromDate', JSON.stringify(fromDate));
    sessionStorage.setItem('totalDue_toDate', JSON.stringify(toDate));
    sessionStorage.setItem('totalDue_dateBy', JSON.stringify(dateBy));
    sessionStorage.setItem('totalDue_projectId', JSON.stringify(projectId));
    sessionStorage.setItem('totalDue_selectedAssociate', JSON.stringify(selectedAssociate));
    sessionStorage.setItem('totalDue_page', JSON.stringify(page));
    sessionStorage.setItem('totalDue_rowsPerPage', JSON.stringify(rowsPerPage));
  }, [search, associateId, sortBy, fromDate, toDate, dateBy, projectId, selectedAssociate, page, rowsPerPage]);

  // Sync temp state when More Filters dialog opens
  useEffect(() => {
    if (moreFiltersOpen) {
      setTempSortBy(sortBy);
      setTempFromDate(fromDate || '');
      setTempToDate(toDate || '');
      setTempDateBy(dateBy || '');
      setTempProjectId(projectId || 'all');
    }
  }, [moreFiltersOpen, sortBy, fromDate, toDate, dateBy, projectId]);

  // Fetch Associates for Autocomplete dropdown with infinite scroll (10 at a time)
  const fetchAssociatesList = useCallback(async (searchQuery = '', pageNum = 1, append = false) => {
    setLoadingAssociates(true);
    try {
      const response = await getAssociates({ page: pageNum, limit: 10, filterKey: searchQuery || undefined });
      if (response && response.status && Array.isArray(response.data)) {
        const newData = response.data;
        setAssociates(prev => {
          const combined = append ? [...prev, ...newData] : newData;
          const total = response.extra?.total || 0;
          setHasMoreAssociates(combined.length < total);
          return combined;
        });
      }
    } catch (err) {
      console.error('Failed to fetch associates for filter:', err);
    } finally {
      setLoadingAssociates(false);
    }
  }, []);

  // Fetch Due Bookings
  const fetchDueBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');
      
      // Parse Sort parameters
      let apiSortBy = associateId === 'all' ? 'totalDue' : 'bookingDate';
      let apiSortOrder = 'desc';
      if (sortBy) {
        if (sortBy.endsWith('Desc')) {
          apiSortBy = sortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (sortBy.endsWith('Asc')) {
          apiSortBy = sortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        } else {
          apiSortBy = sortBy;
        }
      }

      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        associateId: associateId,
        sortBy: apiSortBy,
        sort: apiSortOrder,
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      if (fromDate) {
        params.append('fromDate', fromDate);
      }

      if (toDate) {
        params.append('toDate', toDate);
      }

      if (dateBy) {
        params.append('dateBy', dateBy);
      }

      if (projectId && projectId !== 'all') {
        params.append('projectId', projectId);
      }

      const isAssociatesMode = associateId === 'all';
      const endpoint = isAssociatesMode 
        ? `${base}/api/bookings/due-associates`
        : `${base}/api/bookings/due-bookings`;

      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || `Failed to fetch due ${isAssociatesMode ? 'associates' : 'bookings'}`);
      }

      setBookings(Array.isArray(result.data) ? result.data : []);
      if (result.summary) {
        setSummary(result.summary);
      } else if (result.extra?.summary) {
        setSummary(result.extra.summary);
      } else {
        setSummary({
          totalAmount: 0,
          totalBookingAmount: 0,
          totalReceived: 0,
          totalDue: 0,
        });
      }
      if (result.extra) {
        setTotalCount(result.extra.total || 0);
      } else {
        setTotalCount(result.data?.length || 0);
      }
    } catch (err) {
      console.error('Failed to load due bookings:', err);
      setError(err.message || 'Failed to load due bookings');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, associateId, sortBy, search, fromDate, toDate, dateBy, projectId]);

  useEffect(() => {
    setAssociatesPage(1);
    setHasMoreAssociates(true);
    fetchAssociatesList(associatesSearch, 1, false);
  }, [associatesSearch, fetchAssociatesList]);

  useEffect(() => {
    fetchDueBookings();
  }, [fetchDueBookings]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        if (Array.isArray(data)) {
          setProjects(data);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, []);

  const handleResetFilters = () => {
    setSearch('');
    setSelectedAssociate(null);
    setAssociateId('all');
    setAssociatesSearch('');
    setSortBy('');
    setFromDate('');
    setToDate('');
    setDateBy('');
    setProjectId('all');
    setPage(0);
  };

  const handleSort = (field) => {
    const descVal = field + 'Desc';
    const ascVal = field + 'Asc';
    if (sortBy === descVal) {
      setSortBy(ascVal);
    } else {
      setSortBy(descVal);
    }
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {associateId !== 'all' && (
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedAssociate(null);
                setAssociateId('all');
                setPage(0);
                setSortBy('');
                setFromDate('');
                setToDate('');
                setDateBy('');
                setProjectId('all');
              }}
              sx={{ borderRadius: 2, minWidth: 'auto', p: 1 }}
            >
              <ArrowBack />
            </Button>
          )}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              {associateId === 'all' ? 'Total Due Associates' : `Due Bookings - ${selectedAssociate?.name || ''}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {associateId === 'all' 
                ? 'View outstanding payment summaries grouped by associates' 
                : 'View and track outstanding payments for this associate'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Refresh />}
          onClick={fetchDueBookings}
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            title: 'Total Booked Property Value',
            value: summary.totalAmount,
            color: '#1976d2',
            icon: <Business sx={{ color: '#1976d2', fontSize: 32 }} />,
            sortField: 'propertyAmount',
          },
          // {
          //   title: 'Total Booking Amount',
          //   value: summary.totalBookingAmount,
          //   color: '#ff9800',
          //   icon: <Payments sx={{ color: '#ff9800', fontSize: 32 }} />,
          // },
          {
            title: 'Total Received Amount',
            value: summary.totalReceived,
            color: '#2e7d32',
            icon: <Receipt sx={{ color: '#2e7d32', fontSize: 32 }} />,
            sortField: 'totalReceived',
          },
          {
            title: 'Total Outstanding Due',
            value: summary.totalDue,
            color: '#d32f2f',
            icon: <Payments sx={{ color: '#d32f2f', fontSize: 32 }} />,
            sortField: 'totalDueAmount',
          },
        ].map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderLeft: `6px solid ${card.color}`,
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  {card.title}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                  ₹{(card.value || 0).toLocaleString('en-IN')}
                </Typography>
              </Box>
              <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', p: 1.5, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {card.icon}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters Card */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4.5}>
            <TextField
              fullWidth
              size="small"
              placeholder={associateId === 'all' ? "Search by name, phone, ID..." : "Search by customer name, project..."}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4.5}>
            <Autocomplete
              size="small"
              options={associates}
              getOptionLabel={(option) => option ? option.name || '' : ''}
              loading={loadingAssociates}
              value={selectedAssociate}
              onChange={(event, newValue) => {
                setSelectedAssociate(newValue);
                setAssociateId(newValue ? newValue._id : 'all');
                setPage(0);
                if (newValue) {
                  setSortBy('bookingDateDesc');
                } else {
                  setSortBy('totalDueDesc');
                }
              }}
              inputValue={associatesSearch || ''}
              onInputChange={(event, newInputValue) => {
                setAssociatesSearch(newInputValue || '');
              }}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderOption={(props, option) => {
                const phone = option.phone || option.whatsapp || option.mobile || '';
                const code = option.loginId || option.associateId || option.code || '';
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {phone ? `Mobile: ${phone}` : ''} {code ? ` | ID: ${code}` : ''}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Associate"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              ListboxProps={{
                style: { maxHeight: '300px' },
                onScroll: (event) => {
                  const listboxNode = event.currentTarget;
                  const scrollPercentage = (listboxNode.scrollTop + listboxNode.clientHeight) / listboxNode.scrollHeight;

                  if (
                    scrollPercentage >= 0.8 &&
                    hasMoreAssociates &&
                    !loadingAssociates
                  ) {
                    const nextPage = associatesPage + 1;
                    setAssociatesPage(nextPage);
                    fetchAssociatesList(associatesSearch, nextPage, true);
                  }
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <MoreFilters
              expanded={moreFiltersOpen}
              onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
              sortBy={tempSortBy}
              onChangeSortBy={setTempSortBy}
              onApply={() => {
                setSortBy(tempSortBy);
                setFromDate(tempFromDate);
                setToDate(tempToDate);
                setDateBy(tempDateBy);
                setProjectId(tempProjectId);
                setPage(0);
              }}
              onReset={() => {
                setTempSortBy('');
                setTempFromDate('');
                setTempToDate('');
                setTempDateBy('');
                setTempProjectId('all');
              }}
              type={associateId === 'all' ? "totalDue" : "totalDueBookings"}
              height="40px"
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Project"
                    value={tempProjectId}
                    onChange={(e) => setTempProjectId(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="all">All Projects</MenuItem>
                    {projects.map((proj) => (
                      <MenuItem key={proj._id} value={proj._id}>
                        {proj.projectName || proj.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From Date"
                    value={tempFromDate}
                    onChange={(e) => setTempFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    value={tempToDate}
                    onChange={(e) => setTempToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Date By"
                    value={tempDateBy}
                    onChange={(e) => setTempDateBy(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </MoreFilters>
          </Grid>
        </Grid>
      </Paper>

      {/* Bookings Table */}
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : bookings.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No due bookings found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#536DFE' }}>
              {associateId === 'all' ? (
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Login ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Associate Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => handleSort('totalAmount')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Property Amount <SwapVert sx={{ fontSize: 16 }} />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => handleSort('totalReceived')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Received <SwapVert sx={{ fontSize: 16 }} />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => handleSort('totalDue')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Due Amount <SwapVert sx={{ fontSize: 16 }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Login ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Associate Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Booking ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Project / Inventory</TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => handleSort('propertyAmount')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Property Amount <SwapVert sx={{ fontSize: 16 }} />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => handleSort('totalDueAmount')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Due Amount <SwapVert sx={{ fontSize: 16 }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Received</TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => handleSort('bookingDate')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Booking Date <SwapVert sx={{ fontSize: 16 }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {bookings.map((item) => (
                <TableRow key={item._id || item.associate?.associateId} hover>
                  {associateId === 'all' ? (
                    <React.Fragment>
                      <TableCell>
                        {item.associate?.loginId || item.associate?.associateId || item.associate?.code || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {item.associate ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => {
                              if (item.associate) {
                                const assoc = {
                                  ...item.associate,
                                  _id: item.associate.associateId,
                                };
                                setSelectedAssociate(assoc);
                                setAssociateId(item.associate.associateId);
                                setPage(0);
                                setSortBy('bookingDate');
                              }
                            }}
                          >
                            {item.associate.name || 'N/A'}
                          </Typography>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {item.associate?.phone || item.associate?.whatsapp || item.associate?.mobile || 'N/A'}
                      </TableCell>
                      <TableCell>₹{(item.totalAmount || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        ₹{(item.totalReceived || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ color: '#d32f2f', fontWeight: 700 }}>
                        ₹{(item.totalDue || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => {
                            if (item.associate) {
                              const assoc = {
                                ...item.associate,
                                _id: item.associate.associateId,
                              };
                              setSelectedAssociate(assoc);
                              setAssociateId(item.associate.associateId);
                              setPage(0);
                              setSortBy('bookingDate');
                            }
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <TableCell>
                        {item.associate?.loginId || item.associate?.associateId || item.associate?.code || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {item.associate?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {item.associate?.phone || item.associate?.whatsapp || item.associate?.mobile || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {item.customer?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.customer?.phone || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => {
                            if (item._id) {
                              history.push(`/app/booking/BookingDetails/${item._id}`);
                            }
                          }}
                        >
                          #{item.bookingId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.particular?.projectName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.particular?.inventoryType || 'Plot'}: {item.particular?.inventoryDetail || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>₹{(item.propertyAmount || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ color: '#d32f2f', fontWeight: 700 }}>
                        ₹{(item.totalDueAmount || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        ₹{(item.totalReceived || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>
                        {item.bookingDate ? new Date(item.bookingDate).toLocaleDateString('en-IN') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.approvedStatus || 'booked'}
                          size="small"
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            backgroundColor:
                              item.approvedStatus === 'approved' || item.approvedStatus === 'booked'
                                ? 'rgba(46, 125, 50, 0.12)'
                                : 'rgba(211, 47, 47, 0.12)',
                            color:
                              item.approvedStatus === 'approved' || item.approvedStatus === 'booked'
                                ? '#2e7d32'
                                : '#d32f2f',
                          }}
                        />
                      </TableCell>
                    </React.Fragment>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default TotalDue;
